import React, { useState, useEffect } from 'react'
import client from '@/api/client'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { TapScorecardTemplate, TapScorecardCompetency } from '@/types/index'
import { AlertCircle, Pencil, Trash2, Check, X } from 'lucide-react'

// Backend returns ScorecardTemplateListResponse (PaginatedResponse) with a
// snake_case body. Unwrap and map once so the component deals in camelCase.
interface RawTemplate {
  id: string
  name: string
  description?: string | null
  is_active?: boolean | null
}

interface RawTemplateList {
  items?: RawTemplate[]
  total?: number
}

interface RawCompetency {
  id: string
  template_id: string
  name: string
  description?: string | null
  weight: number
  sort_order: number
  is_active?: boolean | null
}

const mapTemplate = (t: RawTemplate): TapScorecardTemplate => ({
  id: t.id,
  name: t.name,
  description: t.description ?? undefined,
  isActive: Boolean(t.is_active),
})

const mapCompetency = (c: RawCompetency): TapScorecardCompetency => ({
  id: c.id,
  templateId: c.template_id,
  name: c.name,
  description: c.description ?? undefined,
  weight: c.weight,
  sortOrder: c.sort_order,
  isActive: Boolean(c.is_active),
})

interface ScorecardTemplatesProps {
  embedded?: boolean
}

const ScorecardTemplates: React.FC<ScorecardTemplatesProps> = ({ embedded = false }) => {
  const [templates, setTemplates] = useState<TapScorecardTemplate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<TapScorecardTemplate | null>(null)
  const [competencies, setCompetencies] = useState<TapScorecardCompetency[]>([])

  const [formData, setFormData] = useState({ name: '', description: '' })
  const [competencyForm, setCompetencyForm] = useState({ name: '', description: '', weight: 1 })

  // Bug #228: per-row edit state. We track which competency is in edit mode
  // and the staged values, so the user can adjust name/description/weight
  // before committing the PATCH.
  const [editingCompetencyId, setEditingCompetencyId] = useState<string | null>(null)
  const [editCompetencyForm, setEditCompetencyForm] = useState({ name: '', description: '', weight: 1 })

  const fetchTemplates = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await client.get<RawTemplateList>('/tap/scorecard-templates')
      const items = response.data?.items ?? []
      setTemplates(items.map(mapTemplate))
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch templates'))
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCompetencies = async (templateId: string) => {
    try {
      const response = await client.get<RawCompetency[]>(`/tap/scorecard-templates/${templateId}/competencies`)
      const rawItems = Array.isArray(response.data) ? response.data : []
      setCompetencies(rawItems.map(mapCompetency))
    } catch {
      setCompetencies([])
    }
  }

  useEffect(() => {
    fetchTemplates()
  }, [])

  const handleCreate = async () => {
    if (!formData.name) {
      alert('Template name is required')
      return
    }
    try {
      if (editingTemplate) {
        await client.patch(`/tap/scorecard-templates/${editingTemplate.id}`, formData)
      } else {
        await client.post('/tap/scorecard-templates', formData)
      }
      setCreateModalOpen(false)
      setEditingTemplate(null)
      setFormData({ name: '', description: '' })
      fetchTemplates()
    } catch {
      alert('Failed to save template')
    }
  }

  const handleDelete = async (template: TapScorecardTemplate) => {
    if (!confirm(`Delete template "${template.name}"?`)) return
    try {
      await client.delete(`/tap/scorecard-templates/${template.id}`)
      fetchTemplates()
    } catch {
      alert('Failed to delete template')
    }
  }

  const handleAddCompetency = async () => {
    if (!editingTemplate || !competencyForm.name) return
    try {
      // Bug #150: we pass `sort_order` explicitly so the ordering matches the
      // current view; the backend will also fall back to end-of-list if this
      // ever becomes optional on the UI. `template_id` is supplied via the
      // URL path — it was previously also required in the body, which was the
      // root cause of the 422.
      await client.post(`/tap/scorecard-templates/${editingTemplate.id}/competencies`, {
        name: competencyForm.name,
        description: competencyForm.description || undefined,
        weight: competencyForm.weight,
        sort_order: competencies.length,
      })
      setCompetencyForm({ name: '', description: '', weight: 1 })
      fetchCompetencies(editingTemplate.id)
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      alert(
        typeof detail === 'string'
          ? `Failed to add competency: ${detail}`
          : 'Failed to add competency',
      )
    }
  }

  const handleViewDetail = async (template: TapScorecardTemplate) => {
    setEditingTemplate(template)
    await fetchCompetencies(template.id)
    setDetailModalOpen(true)
  }

  // Bug #228: enter edit mode for an existing competency. Stages the current
  // values into editCompetencyForm so the user can adjust them before saving.
  const handleStartEditCompetency = (c: TapScorecardCompetency) => {
    setEditingCompetencyId(c.id)
    setEditCompetencyForm({
      name: c.name,
      description: c.description || '',
      weight: c.weight,
    })
  }

  const handleCancelEditCompetency = () => {
    setEditingCompetencyId(null)
    setEditCompetencyForm({ name: '', description: '', weight: 1 })
  }

  const handleSaveEditCompetency = async (competencyId: string) => {
    if (!editingTemplate || !editCompetencyForm.name) return
    try {
      await client.patch(
        `/tap/scorecard-templates/${editingTemplate.id}/competencies/${competencyId}`,
        {
          name: editCompetencyForm.name,
          description: editCompetencyForm.description || null,
          weight: editCompetencyForm.weight,
        },
      )
      handleCancelEditCompetency()
      await fetchCompetencies(editingTemplate.id)
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? `Failed to save competency: ${detail}` : 'Failed to save competency')
    }
  }

  const handleDeleteCompetency = async (c: TapScorecardCompetency) => {
    if (!editingTemplate) return
    if (!confirm(`Remove competency "${c.name}"?`)) return
    try {
      await client.delete(`/tap/scorecard-templates/${editingTemplate.id}/competencies/${c.id}`)
      // If the user was editing this row, clear the edit state too.
      if (editingCompetencyId === c.id) handleCancelEditCompetency()
      await fetchCompetencies(editingTemplate.id)
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? `Failed to remove competency: ${detail}` : 'Failed to remove competency')
    }
  }

  // Bug #228: live total of competency weights. The work item requires this
  // to equal 100 before the template is considered "ready to use".
  const totalWeight = competencies.reduce((sum, c) => sum + (c.weight || 0), 0)
  const weightsAreValid = totalWeight === 100

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '4px',
  }

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: 'var(--color-magenta)',
    color: 'var(--color-white)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  }

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '8px 16px',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '6px',
    backgroundColor: 'var(--color-white)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  }

  const columns = [
    {
      key: 'name' as keyof TapScorecardTemplate,
      header: 'Name',
      render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div>,
    },
    {
      key: 'description' as keyof TapScorecardTemplate,
      header: 'Description',
      render: (value: string | undefined) => value || '-',
    },
    {
      key: 'isActive' as keyof TapScorecardTemplate,
      header: 'Status',
      render: (value: boolean) => (
        <span style={{
          display: 'inline-block', padding: '4px 10px', borderRadius: '4px',
          fontSize: '12px', fontWeight: 600,
          backgroundColor: value ? '#dbeafe' : '#f3f4f6',
          color: value ? '#0284c7' : '#6b7280',
        }}>
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ]

  return (
    <div style={{ padding: '0' }}>
      {!embedded ? (
        <PageHeader
          title="Scorecard Templates"
          subtitle="Manage interview scorecard templates and competencies"
          actions={
            <button
              onClick={() => {
                setEditingTemplate(null)
                setFormData({ name: '', description: '' })
                setCreateModalOpen(true)
              }}
              style={buttonStyle}
            >
              + New Template
            </button>
          }
        />
      ) : (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 24px 0 24px' }}>
          <button
            onClick={() => {
              setEditingTemplate(null)
              setFormData({ name: '', description: '' })
              setCreateModalOpen(true)
            }}
            style={buttonStyle}
          >
            + New Template
          </button>
        </div>
      )}

      <div style={{ padding: '24px' }}>
        {error && (
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
            padding: '12px 16px', marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading templates</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error.message}</p>
            </div>
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading templates...
          </div>
        ) : templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            No scorecard templates found.
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={templates}
            rowKey="id"
            actions={[
              { label: 'View', onClick: (row: TapScorecardTemplate) => handleViewDetail(row) },
              {
                label: 'Edit',
                onClick: (row: TapScorecardTemplate) => {
                  setEditingTemplate(row)
                  setFormData({ name: row.name, description: row.description || '' })
                  setCreateModalOpen(true)
                },
              },
              { label: 'Delete', onClick: (row: TapScorecardTemplate) => handleDelete(row), variant: 'danger' as const },
            ]}
          />
        )}
      </div>

      {/* Create/Edit modal */}
      <Modal
        isOpen={createModalOpen}
        title={editingTemplate ? 'Edit Template' : 'New Template'}
        onClose={() => {
          setCreateModalOpen(false)
          setEditingTemplate(null)
        }}
        isDirty={formData.name !== '' || formData.description !== ''}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => { setCreateModalOpen(false); setEditingTemplate(null) }} style={secondaryButtonStyle}>
              Cancel
            </button>
            <button onClick={handleCreate} style={buttonStyle}>
              {editingTemplate ? 'Save' : 'Create'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Template name (required)</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>

      {/* Detail modal with competencies */}
      <Modal
        isOpen={detailModalOpen}
        title={editingTemplate ? `Template: ${editingTemplate.name}` : 'Template'}
        onClose={() => { setDetailModalOpen(false); setEditingTemplate(null); handleCancelEditCompetency() }}
        size="lg"
        isDirty={competencyForm.name !== '' || competencyForm.description !== ''}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => { setDetailModalOpen(false); setEditingTemplate(null); handleCancelEditCompetency() }} style={secondaryButtonStyle}>
              Close
            </button>
          </div>
        }
      >
        <div>
          {editingTemplate?.description && (
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: 'var(--color-dark-grey)' }}>
              {editingTemplate.description}
            </p>
          )}

          <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: 'var(--color-black)' }}>
            Competencies
          </h4>

          {competencies.length === 0 ? (
            <p style={{ color: 'var(--color-dark-grey)', fontSize: '14px' }}>No competencies defined yet.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-family)', marginBottom: '16px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-light-grey)' }}>Name</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-light-grey)' }}>Description</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-light-grey)' }}>Weight</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase', borderBottom: '1px solid var(--color-light-grey)', width: '90px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {competencies.map((c) => {
                  const isEditing = editingCompetencyId === c.id
                  return (
                    <tr key={c.id}>
                      <td style={{ padding: '8px 12px', fontSize: '14px', borderBottom: '1px solid var(--color-light-grey)' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editCompetencyForm.name}
                            onChange={(e) => setEditCompetencyForm({ ...editCompetencyForm, name: e.target.value })}
                            style={{ ...inputStyle, padding: '4px 8px' }}
                          />
                        ) : (
                          c.name
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: '14px', color: 'var(--color-dark-grey)', borderBottom: '1px solid var(--color-light-grey)' }}>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editCompetencyForm.description}
                            onChange={(e) => setEditCompetencyForm({ ...editCompetencyForm, description: e.target.value })}
                            style={{ ...inputStyle, padding: '4px 8px' }}
                            placeholder="Optional"
                          />
                        ) : (
                          c.description || '-'
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: '14px', textAlign: 'right', fontWeight: 600, borderBottom: '1px solid var(--color-light-grey)' }}>
                        {isEditing ? (
                          <input
                            type="number"
                            min={1}
                            value={editCompetencyForm.weight}
                            onChange={(e) => setEditCompetencyForm({ ...editCompetencyForm, weight: Number(e.target.value) })}
                            style={{ ...inputStyle, padding: '4px 8px', textAlign: 'right', width: '70px' }}
                          />
                        ) : (
                          c.weight
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: '14px', textAlign: 'right', borderBottom: '1px solid var(--color-light-grey)' }}>
                        {isEditing ? (
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button
                              onClick={() => handleSaveEditCompetency(c.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#047857' }}
                              title="Save changes"
                              disabled={!editCompetencyForm.name || editCompetencyForm.weight < 1}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={handleCancelEditCompetency}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-dark-grey)' }}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'inline-flex', gap: '6px' }}>
                            <button
                              onClick={() => handleStartEditCompetency(c)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-dark-grey)' }}
                              title="Edit competency"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteCompetency(c)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-magenta)' }}
                              title="Remove competency"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              {/* Bug #228: live total + validation hint. Required to be 100
                  before the template is considered ready (see #177 picker). */}
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ padding: '12px', fontSize: '13px', color: weightsAreValid ? '#047857' : '#b91c1c', fontWeight: 600 }}>
                    {weightsAreValid
                      ? 'Weights total 100 — template is ready to use.'
                      : `Weights must total 100 before this template can be used (currently ${totalWeight}).`}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px', textAlign: 'right', fontWeight: 700, color: weightsAreValid ? '#047857' : '#b91c1c' }}>
                    {totalWeight} / 100
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}

          {/* Add competency inline form */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '12px' }}>
            <div style={{ flex: 2 }}>
              <label style={{ ...labelStyle, fontSize: '12px' }}>Competency name</label>
              <input
                type="text"
                value={competencyForm.name}
                onChange={(e) => setCompetencyForm({ ...competencyForm, name: e.target.value })}
                style={inputStyle}
                placeholder="e.g. Communication"
              />
            </div>
            <div style={{ flex: 2 }}>
              <label style={{ ...labelStyle, fontSize: '12px' }}>Description</label>
              <input
                type="text"
                value={competencyForm.description}
                onChange={(e) => setCompetencyForm({ ...competencyForm, description: e.target.value })}
                style={inputStyle}
                placeholder="Optional"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ ...labelStyle, fontSize: '12px' }}>Weight</label>
              <input
                type="number"
                min={1}
                value={competencyForm.weight}
                onChange={(e) => setCompetencyForm({ ...competencyForm, weight: Number(e.target.value) })}
                style={inputStyle}
              />
            </div>
            <button onClick={handleAddCompetency} style={{ ...buttonStyle, whiteSpace: 'nowrap' }}>
              Add
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default ScorecardTemplates
